import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import { Field as FormikField, useFormikContext } from 'formik'
import { FileMutationDto } from '../../../config/fileConfig'
import FolderField, { DRIVE_ITEM_CONFIG, getFullPath } from '../../../components/TextField/Folder/FolderField'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import FormContainer from '../../../components/Form/FormContainer'
import { PivotProps } from './FileForm'
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Checkbox, Field } from '@fluentui/react-components'
import FileStatusPicker from '../../TagPicker/FileStatusPicker'
import { useTranslation } from 'react-i18next'
import LocalDatePicker from '../../DatePicker/LocalDatePicker'
import UserPicker from '../../TagPicker/UserPicker'
import UserCombobox from '../../Combobox/UserCombobox'
import LanguageCombobox from '../../Combobox/i18n/LanguageCombobox'
import { useSettings } from '../../../hooks/useSettings'
import FormikTextareaField from '../../Formik/FormikTextareaField'
import FormikInputField from '../../Formik/FormikInputField'
import useModules from '../../../hooks/module/useModules'
import { ModuleType } from '../../../context/GlobalContext'
import WorkflowPicker from '../../TagPicker/WorkflowPicker'
import { useApplicationStyles } from '../../../pages/App'
import FormattedDateTime from '../../Date/FormattedDateTime'
import { TimeFormats } from '../../../helpers/dateHelper'
import OrganisationProfileCombobox from '../../Combobox/OrganisationProfileCombobox'
import useOrganisationProfiles from '../../../hooks/useOrganisationProfiles'
import PartyCombobox from '../../Combobox/PartyCombobox'
import { ListItemDto } from '../../../config/partyConfig'
import { IGraphResponse } from '../../../context/AxiosContext'
import useAxios from '../../../hooks/useAxios'

export default function GeneralPivotItem(props: PivotProps): ReactElement {
    const appStyles = useApplicationStyles()
    const axios = useAxios()
    const { loaded: organisationProfilesLoaded, activeOrganisationProfiles, getOrganisationProfile } = useOrganisationProfiles()
    const { settings } = useSettings()
    const modules = useModules()
    const { t: translate } = useTranslation()

    const { errors, values, setFieldValue, status, setStatus, initialValues } = useFormikContext<FileMutationDto>()
    const [crmRelation, setCRMRelation] = useState<ListItemDto>()
    const [crmRelationPath, setCRMRelationPath] = useState<string>()
    const [suggestedPath, setSuggestedPath] = useState<string>()

    useEffect(() => {
        if (!crmRelation && initialValues.crmRelationId) {
            axios.get(`/party/${initialValues.crmRelationId}`).then(result => setCRMRelation(result.data))
        } else if (crmRelation) {
            axios
                .get(`/items/${crmRelation.documentRootId}`, DRIVE_ITEM_CONFIG)
                .then((response: IGraphResponse<DriveItem>) => {
                    if (response.data.folder !== undefined) {
                        // We found a folder that exists, but is free to use.
                        setCRMRelationPath(getFullPath(response.data))
                    } else {
                        setCRMRelationPath(undefined)
                    }
                })
                .catch(() => {
                    setCRMRelationPath(undefined)
                })
        }
    }, [initialValues.crmRelationId, crmRelation])

    useEffect(() => {
        if (initialValues.reference && !initialValues.documentRootId && settings?.fileSharepointDisplayName) {
            setSuggestedPath(
                [
                    crmRelationPath,
                    settings?.fileSharepointDisplayName
                        .replaceAll(/{{\s*file\.name\s*}}/g, values.name)
                        .replaceAll(/{{\s*file\.reference\s*}}/g, values.reference)
                        .replaceAll(/[/\\]/g, '-')
                        .trim(),
                ]
                    .filter(Boolean)
                    .join('/'),
            )
        }
    }, [values.reference, values.name, settings, crmRelationPath])

    useEffect(() => {
        if (values.organisationProfileKey) {
            setFieldValue('unit', getOrganisationProfile(values.organisationProfileKey)?.unit)
        }
    }, [values.organisationProfileKey])

    return (
        <FormContainer>
            <Field label={translate('file.edit.form.general.ownerId')} required orientation={props.orientation}>
                <UserCombobox required selectedOptions={[values.ownerId]} onOptionSelect={(_, data) => setFieldValue('ownerId', data.optionValue)} />
            </Field>
            <Field label={translate('file.edit.form.general.executiveIds')} orientation={props.orientation}>
                <UserPicker selectedOptions={values.executiveIds || []} onOptionsSelect={(_, data) => setFieldValue('executiveIds', data)} />
            </Field>
            {organisationProfilesLoaded && activeOrganisationProfiles?.length > 0 && (
                <Field label={translate('file.edit.form.general.organisationprofileid')} orientation={props.orientation}>
                    <OrganisationProfileCombobox
                        selectedOptions={[values.organisationProfileKey]}
                        onOptionSelect={(_, data) => setFieldValue('organisationProfileKey', data.optionValue)}
                    />
                </Field>
            )}
            {modules.has(ModuleType.Workflow) && (
                <Field label={translate('file.edit.form.general.workflow')} orientation={props.orientation}>
                    <WorkflowPicker
                        selectedOptions={values.workflows || []}
                        onOptionsSelect={(_, selectedOptions) => setFieldValue('workflows', selectedOptions)}
                    />
                </Field>
            )}
            {modules.has(ModuleType.CRM) && (
                <>
                    <Field label={translate('file.edit.form.general.kyc')} orientation={props.orientation}>
                        <Checkbox
                            checked={values.kyc ?? false}
                            disabled={!!values.crmRelationId}
                            onChange={(_, data) => {
                                setFieldValue('kyc', data.checked)
                                setFieldValue('crmRelationId', undefined)
                            }}
                        />
                    </Field>
                    <Field label={translate('file.edit.form.general.crmrelationid')} orientation={props.orientation}>
                        <PartyCombobox
                            selectedOptions={[values.crmRelationId]}
                            disabled={!!values.kyc}
                            onOptionSelect={(_, data, selectedEntities) => {
                                setFieldValue('kyc', false)
                                setFieldValue('crmRelationId', data.optionValue)
                                setCRMRelation(selectedEntities?.[0])
                            }}
                            params={{ crmRelation: true }}
                        />
                    </Field>
                </>
            )}
            <FormikField name="documentRootId" validate={() => status.documentRootErrorMessage}>
                {() => (
                    <FolderField
                        initialItemId={initialValues.documentRootId}
                        suggestedPath={suggestedPath}
                        onChangePath={(path: string, item?: DriveItem, errorMessage?: string) => {
                            setStatus({
                                ...(status || {}),
                                documentRootPath: !errorMessage ? path : undefined,
                                documentRootErrorMessage: errorMessage,
                            })

                            setFieldValue('documentRootId', item?.id)
                        }}
                        textFieldProps={{ required: true, errorMessage: errors?.documentRootId }}
                    />
                )}
            </FormikField>
            {settings.documentsSharing?.enabled && (
                <FormikInputField
                    labelKey={'file.edit.form.general.shareddocumentrooturl'}
                    labelKeyProps={{ name: settings.documentsSharing?.name }}
                    hintKey={'file.edit.form.general.shareddocumentrooturl.hint'}
                    hintKeyProps={{ name: settings.documentsSharing?.name }}
                    fieldName={'sharedDocumentRootURL'}
                />
            )}
            <Accordion collapsible>
                <AccordionItem value="other">
                    <AccordionHeader>{translate('file.edit.form.general.other')}</AccordionHeader>
                    <AccordionPanel>
                        <div className={appStyles.accordionContent}>
                            <Field label={translate('file.edit.form.general.status')} required orientation={props.orientation}>
                                <FileStatusPicker
                                    required
                                    singleSelect
                                    selectedOptions={[values.status]}
                                    onOptionsSelect={(_, selectedOptions) => setFieldValue('status', selectedOptions?.[0])}
                                />
                            </Field>
                            <Field label={translate('file.edit.form.general.language')} required orientation={props.orientation}>
                                <LanguageCombobox
                                    required
                                    selectedOptions={[values.language]}
                                    onOptionSelect={(_, data) => setFieldValue('language', data.selectedOptions?.[0])}
                                />
                            </Field>
                            <FormikTextareaField<FileMutationDto>
                                labelKey="file.edit.form.general.notification.label"
                                hintKey="file.edit.form.general.notification.hint"
                                fieldName="notification"
                                orientation={props.orientation}
                            />
                            <FormikInputField<FileMutationDto>
                                labelKey="file.edit.form.general.archivelocation"
                                fieldName="archiveLocation"
                                orientation={props.orientation}
                            />
                            <Field label={translate('file.edit.form.general.dateincident')} orientation={props.orientation}>
                                <LocalDatePicker
                                    isoValue={values.dateOfIncident}
                                    onSelectDate={date => setFieldValue('dateOfIncident', date)}
                                    maxDate={new Date()}
                                />
                            </Field>
                            <Field label={translate('file.edit.form.general.created')} orientation={props.orientation}>
                                <FormattedDateTime date={values.created} format={TimeFormats.Full} />
                            </Field>
                        </div>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        </FormContainer>
    )
}
